import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Save, Loader2 } from "lucide-react";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import LaunchOfferCountdown from "@/components/LaunchOfferCountdown";

// Convert an ISO timestamp -> value string for <input type="datetime-local"> (local time).
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(v: string): string | null {
  if (!v) return null;
  return new Date(v).toISOString();
}

export default function AdminLaunchOffer() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [active, setActive] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("get_launch_offer_schedule");
    if (!error && Array.isArray(data) && data[0]) {
      const row = data[0] as { starts_at: string | null; ends_at: string | null; active: boolean };
      setStartsAt(toLocalInput(row.starts_at));
      setEndsAt(toLocalInput(row.ends_at));
      setActive(!!row.active);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!endsAt) {
      toast({ title: "End date required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase.rpc("set_launch_offer_schedule", {
      _starts_at: fromLocalInput(startsAt),
      _ends_at: fromLocalInput(endsAt),
      _active: active,
    });
    setSaving(false);
    if (error) {
      toast({
        title: "Couldn't save",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Launch offer schedule updated" });
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHead title="Launch Offer — Admin" description="Configure LAUNCH30 start/end." path="/admin/launch-offer" />
      <div className="container mx-auto max-w-2xl px-4 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </Link>

        <div className="mt-4 mb-6 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h1 className="font-display text-2xl font-bold">Launch Offer Schedule</h1>
          <Badge variant="secondary">LAUNCH30</Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Start & end dates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="starts_at">Starts at (leave blank = always started)</Label>
                  <Input
                    id="starts_at"
                    type="datetime-local"
                    value={startsAt}
                    onChange={(e) => setStartsAt(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ends_at">Ends at</Label>
                  <Input
                    id="ends_at"
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    After this moment the coupon is rejected by the backend and the public
                    countdown disappears.
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border/60 p-3">
                  <div>
                    <Label htmlFor="active" className="text-sm font-medium">Offer active</Label>
                    <p className="text-xs text-muted-foreground">
                      Turn off to disable LAUNCH30 without changing dates.
                    </p>
                  </div>
                  <Switch id="active" checked={active} onCheckedChange={setActive} />
                </div>
                <Button onClick={save} disabled={saving} className="w-full">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" /> Save schedule
                    </>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <div className="mt-8">
          <h2 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Live preview
          </h2>
          <LaunchOfferCountdown />
        </div>
      </div>
    </div>
  );
}
